import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  tap,
} from 'rxjs';
import { AdminService } from './admin.services';
import { selectPermissions } from '../store/auth/auth.reducer';
import { UserPermissions } from '../_helpers/permissions.enum';

/**
 * Answers "may this user do X on this book?" for the admin screens.
 *
 * Two sources have to agree here, and until now only one of them was
 * consulted. The token carries the permissions a user holds everywhere; the
 * book grants carry the permissions they hold on one book in particular. The
 * server has always checked both - the client checked only the token, so a
 * member granted `page.update` on a single book saw the control on no book at
 * all, and the feature was invisible to exactly the people it was built for.
 *
 * The grants are fetched once per session and cached, deliberately not tied to
 * the books list: a deep link straight into a chapters or pages list is a
 * normal way to arrive, and those screens need the same answer. Any component
 * that calls `ensureLoaded()` gets the in-flight or cached request rather than
 * a second round trip.
 */
@Injectable({
  providedIn: 'root',
})
export class BookPermissionsService {
  private readonly adminService = inject(AdminService);
  private readonly store = inject(Store);

  /**
   * Replays so late subscribers - a component created after the response
   * landed - still receive the grants instead of waiting for a change that
   * may never come.
   */
  private readonly grants$ = new ReplaySubject<Record<string, string[]>>(1);

  private request$: Observable<Record<string, string[]>> | null = null;

  private grants: Record<string, string[]> = {};

  private globals: string[] = [];

  constructor() {
    // Kept as a plain field as well as a stream, because the templates ask
    // this question synchronously, once per binding, on every change
    // detection pass.
    this.store.select(selectPermissions).subscribe((permissions) => {
      this.globals = permissions ?? [];
    });

    this.grants$.subscribe((grants) => {
      this.grants = grants;
    });
  }

  /**
   * Fetches the grants unless they are already loaded or in flight.
   *
   * Safe to call from every admin page's ngOnInit - that is the intent. The
   * shared request means five components mounting at once still produce one
   * HTTP call.
   */
  ensureLoaded(): Observable<Record<string, string[]>> {
    if (this.request$) {
      return this.request$;
    }

    this.request$ = this.adminService.getMyBookPermissions().pipe(
      map((response) => response?.Value ?? {}),
      // A failed fetch degrades to global-only permissions rather than
      // breaking the screen. The server still enforces the real answer, so
      // the worst case is a control that is hidden when it could have been
      // shown - recoverable by reloading, unlike an error page.
      catchError(() => of({} as Record<string, string[]>)),
      tap((grants) => this.grants$.next(grants)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.request$;
  }

  /**
   * Discards the cache. Called on logout, so the next user does not inherit
   * the previous one's grants.
   */
  reset(): void {
    this.request$ = null;
    this.grants = {};
    this.grants$.next({});
  }

  /**
   * True when the permission is held globally, or granted on this book.
   *
   * Matches the server's rule exactly: token OR book grant, never both
   * required. Passing no bookId asks the global question only, which is what
   * the layout and translation screens want - they resolve no book, so a book
   * grant could not apply to them even in principle.
   */
  can(permission: string, bookId?: string | null): boolean {
    if (
      this.globals.includes(permission) ||
      this.globals.includes(UserPermissions.ADMIN_ALL)
    ) {
      return true;
    }

    if (!bookId) {
      return false;
    }

    return (this.grants[bookId] ?? []).includes(permission);
  }

  /**
   * True when the permission is held globally or on at least one book.
   *
   * For the gates that guard a whole list rather than one item - the books
   * grid asking whether to render at all. Asking `can('book.read')` with no
   * book there would hide the entire screen from a user whose only access is
   * a grant, which is the same false negative this service exists to fix,
   * just one level up.
   */
  canAny(permission: string): boolean {
    if (this.can(permission)) {
      return true;
    }

    return Object.values(this.grants).some((granted) =>
      granted.includes(permission),
    );
  }

  /**
   * The stream form, for anything that needs to react rather than ask. Emits
   * again whenever either source changes.
   */
  can$(permission: string, bookId?: string | null): Observable<boolean> {
    return combineLatest([
      this.store.select(selectPermissions),
      this.grants$,
    ]).pipe(
      map(([globals, grants]) => {
        if (
          (globals ?? []).includes(permission) ||
          (globals ?? []).includes(UserPermissions.ADMIN_ALL)
        ) {
          return true;
        }

        if (!bookId) {
          return false;
        }

        return (grants[bookId] ?? []).includes(permission);
      }),
    );
  }
}
